import { type Location, type WebConfig } from '@koala/sdk';
import { CONVEYANCE_TYPES } from '@koala/sdk/v4';
import { LOCATION_STATUSES } from '@/constants/locations';
import { supportsBrandId } from '@/utils/config';
import { getLocationId } from '@/utils/locations';
import { encodeURIComponentSafe } from '@/utils/menu';

/** The store's display name, the same source the checkout store card uses. */
export function getLocationName(location: Pick<Location, 'label' | 'cached_data'>): string {
  return location.cached_data?.label ?? location.label ?? '';
}

/** "123 Main St, Chicago, IL 60601"; missing parts are skipped. */
export function formatLocationAddress(
  location: Pick<Location, 'street_address' | 'city' | 'zip_code' | 'cached_data'>,
): string {
  const region = [location.cached_data?.state, location.zip_code].filter(Boolean).join(' ');

  return [location.street_address, location.city, region].filter(Boolean).join(', ');
}

/**
 * "Brand · Location" for the pill. Store labels often already carry the brand
 * ("Crust N Fire Haddonfield"); in that case the label is shown on its own so the
 * brand is not printed twice.
 */
export function getLocationPillLabel(
  location: Pick<Location, 'label' | 'cached_data'>,
  brandLabel?: string | null,
): string {
  const name = getLocationName(location);
  const brand = brandLabel?.trim();

  if (!brand || startsWithWord(name, brand)) {
    return name;
  }

  return `${brand} · ${name}`;
}

/** Whether `text` starts with `word` as a whole word ("Taco Haddonfield" yes, "Tacoma" no). */
function startsWithWord(text: string, word: string): boolean {
  const head = text.slice(0, word.length);
  const next = text.charAt(word.length);

  return head.toLowerCase() === word.toLowerCase() && !/[\p{L}\p{N}]/u.test(next);
}

/** `/store/<id>/<label>`, the URL every store switch navigates to. */
export function getStoreHref(location: Location, webConfig: WebConfig): string {
  // Encoded like `updateStoreMenuUrl`: a "#" in a label would otherwise cut the path.
  return `/store/${getLocationId(location, webConfig)}/${encodeURIComponentSafe(location.label)}`;
}

/**
 * Whether a store can be picked for the diner's current order type. Mirrors the
 * finder: no online ordering (`provider_enabled`), an inactive store, a store
 * without the order type, or (on brand-id storefronts) no brand id to route to.
 */
export function isLocationSelectable(
  location: Location,
  conveyanceType: string | null | undefined,
  webConfig: WebConfig,
): boolean {
  if (location.provider_enabled === false || location.status_id === LOCATION_STATUSES.INACTIVE) {
    return false;
  }

  if (supportsBrandId(webConfig) && !location.brand_id) {
    return false;
  }

  if (conveyanceType === CONVEYANCE_TYPES.DELIVERY) {
    return Boolean(location.supports_delivery);
  }

  // Pickup, curbside and dine-in all need an in-store handoff.
  return location.supports_pick_up;
}
